import { useEffect, useRef, useState } from 'react';
import {
  ArrowIcon, EditIcon, DownloadIcon, RefreshIcon, CopyIcon, MoreIcon,
  HeartIcon, CompareIcon, FilmIcon, CaretIcon,
} from '../../../../components/app-shell/icons';
import { getPrompt, getTimeAgo, downloadMedia } from '../../../../components/GenerationCard';

/* ─── shared button ───────────────────────────────────────────────────────── */

function Action({ icon, label, onClick, tone, disabled, active }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        height: 34, padding: '0 12px', borderRadius: 9, display: 'inline-flex', alignItems: 'center', gap: 7,
        background: active ? 'rgba(255,126,103,.14)' : hov && !disabled ? 'rgba(255,255,255,.12)' : 'rgba(255,255,255,.05)',
        border: `1px solid ${active ? 'rgba(255,126,103,.4)' : 'var(--border)'}`,
        color: tone || (active ? '#FFC2B5' : disabled ? 'var(--text-4)' : 'var(--text-2)'),
        fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? .45 : 1, transition: 'background .12s', whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {icon}{label}
    </button>
  );
}

/* ─── "use as input" with a target picker for multi-slot models ───────────── */

function UseAsInputButton({ imageParams, url, onUseAsInput }) {
  const [open, setOpen] = useState(false);
  const multi = (imageParams?.length || 0) > 1;
  if (!multi) {
    return <Action icon={<ArrowIcon s={13} />} label={imageParams?.[0]?.title ? `Use as ${imageParams[0].title}` : 'Use as input'} onClick={() => onUseAsInput(url, imageParams?.[0]?.paramName)} />;
  }
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <Action icon={<ArrowIcon s={13} />} label="Use as input ▾" onClick={() => setOpen(v => !v)} active={open} />
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 300 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', bottom: '112%', left: 0, minWidth: 170, background: 'rgba(12,12,20,.98)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,.7)', zIndex: 301, overflow: 'hidden' }}>
            {imageParams.map(p => (
              <button key={p.paramName}
                onClick={() => { onUseAsInput(url, p.paramName); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,.85)', fontSize: 12, cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.07)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >{p.title}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FrameButton({ url, onUseVideoFrame }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <Action icon={<FilmIcon s={13} />} label="Use frame ▾" onClick={() => setOpen(v => !v)} active={open} />
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 300 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', bottom: '112%', left: 0, minWidth: 160, background: 'rgba(12,12,20,.98)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,.7)', zIndex: 301, overflow: 'hidden' }}>
            {[['first', 'First frame'], ['last', 'Last frame']].map(([k, l]) => (
              <button key={k}
                onClick={() => { onUseVideoFrame(url, k); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,.85)', fontSize: 12, cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.07)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >{l}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── thumbnail ───────────────────────────────────────────────────────────── */

function Thumb({ item, active, onClick, refCb }) {
  const { output, gen } = item;
  const pending = ['submitting', 'pending', 'processing'].includes(gen.status);
  const failed = gen.status === 'failed';
  return (
    <button
      ref={refCb}
      onClick={onClick}
      title={pending ? 'Generating…' : failed ? (gen.error || 'Failed') : (getPrompt(gen) || gen.model?.model_name || '')}
      style={{
        position: 'relative', width: 72, height: 72, flexShrink: 0, padding: 0, borderRadius: 10, overflow: 'hidden',
        cursor: 'pointer', background: 'rgba(255,255,255,.05)',
        border: `2px solid ${active ? '#FF7E67' : 'rgba(255,255,255,.10)'}`,
        transform: active ? 'translateY(-3px)' : 'none',
        boxShadow: active ? '0 8px 22px -8px rgba(255,126,103,.7)' : 'none',
        transition: 'transform .14s, border-color .14s, box-shadow .14s',
      }}
    >
      {/* Type-gated, not url-gated: a text output can carry prose in `url` from
          older job payloads, and `<img src="{prose}">` fires a bogus request. */}
      {output?.url && output.type === 'video' && (
        <video src={output.url} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {output?.url && output.type !== 'video' && output.type !== 'text' && (
        <img src={output.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {(!output?.url || output.type === 'text') && (
        <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: 9, letterSpacing: '.04em',
          color: failed ? '#f87171' : 'var(--text-4)', background: failed ? 'rgba(245,87,108,.08)' : 'rgba(255,255,255,.03)' }}>
          {failed ? 'FAILED' : pending ? '•••' : 'TEXT'}
        </div>
      )}
      {pending && (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(110deg,transparent 30%,rgba(255,77,141,.22) 50%,transparent 70%)', backgroundSize: '200% 100%', animation: 'gc-shimmer 1.6s linear infinite' }} />
      )}
      {item.outputCount > 1 && (
        <span style={{ position: 'absolute', bottom: 2, right: 3, fontSize: 8, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,.7)', borderRadius: 4, padding: '1px 3px' }}>
          {item.outputIndex + 1}/{item.outputCount}
        </span>
      )}
    </button>
  );
}

/* ─── main ────────────────────────────────────────────────────────────────── */

export default function ZenGalleryView({
  items,
  activeIndex,
  onActiveIndexChange,
  onUseAsInput,
  onUseAsPrompt,
  onUseVideoFrame,
  onEditImage,
  onRerun,
  onViewDetails,
  onToggleLike,
  onCompare,
  onLoadOlder,
  loadingOlder,
  hasMoreOlder,
  likedIds,
  imageParams = [],
  formatCost,
  /** False while a modal owns the arrow keys (detail view, lightbox). */
  keyboardEnabled = true,
}) {
  const stripRef = useRef(null);
  const activeThumbRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const safeIndex = Math.min(Math.max(activeIndex, 0), Math.max(items.length - 1, 0));
  const item = items[safeIndex];

  // Centre the focused thumbnail in the filmstrip.
  // NOT scrollIntoView(): that walks up and scrolls EVERY scrollable ancestor,
  // which dragged the whole app sideways inside `.screen-root`. Scroll the strip
  // and nothing else.
  useEffect(() => {
    const strip = stripRef.current;
    const thumb = activeThumbRef.current;
    if (!strip || !thumb) return;
    // Rect math, not offsetLeft: the strip is statically positioned, so a thumb's
    // offsetParent is some ancestor and offsetLeft is not strip-relative.
    const sRect = strip.getBoundingClientRect();
    const tRect = thumb.getBoundingClientRect();
    const delta = (tRect.left - sRect.left) - (strip.clientWidth - tRect.width) / 2;
    strip.scrollTo({ left: Math.max(0, strip.scrollLeft + delta), behavior: 'smooth' });
  }, [safeIndex, items.length]);

  // ← / → step through outputs; ignored while typing in the prompt
  useEffect(() => {
    if (!keyboardEnabled) return undefined;
    const onKey = e => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); onActiveIndexChange(Math.max(0, safeIndex - 1)); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); onActiveIndexChange(Math.min(items.length - 1, safeIndex + 1)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [safeIndex, items.length, onActiveIndexChange, keyboardEnabled]);

  if (!item) return null;

  const { gen, output } = item;
  const pending = ['submitting', 'pending', 'processing'].includes(gen.status);
  const failed = gen.status === 'failed';
  const isVideo = output?.type === 'video';
  const isText = output?.type === 'text';
  // Older job payloads can carry the text in `url` instead of `content`.
  const textBody = isText ? (output?.content || output?.url || '') : '';
  const prompt = getPrompt(gen);
  const liked = likedIds?.has?.(gen.jobId || gen.id);

  const copyPrompt = () => {
    navigator.clipboard?.writeText(textBody || prompt || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  const stepBtn = dir => (
    <button
      onClick={() => onActiveIndexChange(dir < 0 ? Math.max(0, safeIndex - 1) : Math.min(items.length - 1, safeIndex + 1))}
      disabled={dir < 0 ? safeIndex === 0 : safeIndex >= items.length - 1}
      title={dir < 0 ? 'Previous (←)' : 'Next (→)'}
      style={{
        position: 'absolute', top: '50%', [dir < 0 ? 'left' : 'right']: 10, transform: 'translateY(-50%)',
        width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center',
        background: 'rgba(0,0,0,.6)', border: '1px solid rgba(255,255,255,.16)', color: '#fff',
        cursor: 'pointer', backdropFilter: 'blur(8px)', zIndex: 3,
        opacity: (dir < 0 ? safeIndex === 0 : safeIndex >= items.length - 1) ? .25 : 1,
      }}
    >
      <span style={{ display: 'grid', placeItems: 'center', transform: dir < 0 ? 'rotate(90deg)' : 'rotate(-90deg)' }}><CaretIcon s={14} /></span>
    </button>
  );

  return (
    // minWidth:0 throughout: a flex child defaults to min-width:auto, so the
    // non-shrinking button row below would set the min-content width of the whole
    // column and push the app layout wider than the viewport.
    <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Stage — the focused output, big ───────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 220, position: 'relative', borderRadius: 16, overflow: 'hidden',
        background: 'rgba(255,255,255,.025)', border: '1px solid var(--border)', display: 'grid', placeItems: 'center' }}>

        {items.length > 1 && stepBtn(-1)}
        {items.length > 1 && stepBtn(1)}

        {pending && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, color: 'var(--text-3)' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,.08)', borderTopColor: '#FF7E67', borderRadius: '50%', animation: 'zen-spin 1s linear infinite' }} />
            <span style={{ fontSize: 13 }}>Generating on {gen.model?.model_name || 'model'}…</span>
          </div>
        )}

        {failed && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 32, textAlign: 'center', maxWidth: 520 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(245,87,108,.1)', display: 'grid', placeItems: 'center', color: '#f87171', fontSize: 20 }}>✕</div>
            <div style={{ fontSize: 14, color: '#f87171', fontWeight: 600 }}>Generation failed</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', wordBreak: 'break-word' }}>{gen.error || 'Unknown error'}</div>
            {onRerun && <Action icon={<RefreshIcon s={13} />} label="Load params & retry" onClick={() => onRerun(gen)} />}
          </div>
        )}

        {!pending && !failed && output?.url && !isText && (
          isVideo
            ? <video key={output.url} src={output.url} controls loop playsInline style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }} />
            : <img
                key={output.url}
                src={output.url}
                alt=""
                onClick={() => onViewDetails?.(gen)}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', cursor: 'zoom-in' }}
              />
        )}

        {!pending && !failed && isText && (
          <div className="scroll-y" style={{ padding: 28, maxHeight: '100%', width: '100%' }}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: 'var(--text-2)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{textBody}</p>
          </div>
        )}

        {/* Position counter */}
        <div style={{ position: 'absolute', top: 10, right: 12, background: 'rgba(0,0,0,.7)', borderRadius: 7, padding: '3px 9px', fontSize: 11, color: '#fff', fontWeight: 600, zIndex: 3 }}>
          {safeIndex + 1} / {items.length}
        </div>
      </div>

      {/* ── Meta + actions for the focused output ─────────────────────────── */}
      <div style={{ flexShrink: 0, minWidth: 0 }}>
        <div className="row" style={{ gap: 10, marginBottom: 8, flexWrap: 'wrap', minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{gen.model?.model_name || gen.model?.name || 'Generation'}</span>
          <span style={{ fontSize: 11, color: 'var(--text-4)' }}>·</span>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{getTimeAgo(gen.createdAt || gen.created_at)}</span>
          {gen.cost != null && (
            <>
              <span style={{ fontSize: 11, color: 'var(--text-4)' }}>·</span>
              <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{formatCost ? formatCost(gen.cost) : gen.cost} URO</span>
            </>
          )}
          {item.outputCount > 1 && (
            <span className="chip" style={{ height: 18, fontSize: 10, color: 'var(--text-3)' }}>output {item.outputIndex + 1} of {item.outputCount}</span>
          )}
        </div>

        {prompt && (
          <div title={prompt} style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5, marginBottom: 10, minWidth: 0, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {prompt}
          </div>
        )}

        {/* Wraps instead of scrolling sideways — Like and Details were falling off
            the right edge, which is the exact "where are the buttons" problem. */}
        <div style={{ display: 'flex', gap: 6, rowGap: 6, flexWrap: 'wrap', minWidth: 0, paddingBottom: 2 }}>
          {!pending && !failed && output?.url && !isText && (
            isVideo
              ? onUseVideoFrame && <FrameButton url={output.url} onUseVideoFrame={onUseVideoFrame} />
              : onUseAsInput && <UseAsInputButton imageParams={imageParams} url={output.url} onUseAsInput={onUseAsInput} />
          )}
          {!pending && !failed && output?.url && !isVideo && !isText && onEditImage && (
            <Action icon={<EditIcon s={13} />} label="Edit" onClick={() => onEditImage(output.url)} />
          )}
          {!pending && isText && textBody && onUseAsPrompt && (
            <Action icon={<ArrowIcon s={13} />} label="Use as prompt" onClick={() => onUseAsPrompt(textBody)} />
          )}
          {onRerun && <Action icon={<RefreshIcon s={13} />} label="Reuse params" onClick={() => onRerun(gen)} />}
          {!pending && !failed && output?.url && !isText && (
            <Action icon={<DownloadIcon s={13} />} label="Download" onClick={() => downloadMedia(output.url)} />
          )}
          {!pending && !failed && output?.url && !isText && onCompare && (
            <Action icon={<CompareIcon s={13} />} label="Compare" onClick={() => onCompare(gen, output.url)} />
          )}
          <Action icon={<CopyIcon s={13} />} label={copied ? 'Copied' : 'Copy prompt'} onClick={copyPrompt} tone={copied ? '#10B981' : undefined} />
          {onToggleLike && !pending && !failed && (
            <Action icon={<HeartIcon s={13} filled={liked} />} label={liked ? 'Liked' : 'Like'} onClick={() => onToggleLike(gen)} tone={liked ? '#e63946' : undefined} />
          )}
          {onViewDetails && <Action icon={<MoreIcon s={13} />} label="Details" onClick={() => onViewDetails(gen)} />}
        </div>
      </div>

      {/* ── Filmstrip ─────────────────────────────────────────────────────── */}
      <div ref={stripRef} className="no-scrollbar" style={{ flexShrink: 0, minWidth: 0, display: 'flex', gap: 8, overflowX: 'auto', padding: '12px 2px 2px', borderTop: '1px solid var(--border)' }}>
        {items.map((it, i) => (
          <Thumb
            key={it.key}
            item={it}
            active={i === safeIndex}
            refCb={i === safeIndex ? activeThumbRef : undefined}
            onClick={() => onActiveIndexChange(i)}
          />
        ))}
        {/* The filmstrip is the only way to reach older work in this view. */}
        {onLoadOlder && hasMoreOlder && (
          <button
            onClick={onLoadOlder}
            disabled={loadingOlder}
            title="Load older generations"
            style={{
              width: 72, height: 72, flexShrink: 0, borderRadius: 10, cursor: loadingOlder ? 'wait' : 'pointer',
              background: 'rgba(255,255,255,.03)', border: '1px dashed var(--border-2)', color: 'var(--text-3)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              fontSize: 10, fontWeight: 600, padding: 4, textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>↓</span>
            {loadingOlder ? 'Loading…' : 'Older'}
          </button>
        )}
      </div>
    </div>
  );
}
